'use client';
import { CardListingPosition } from '@hyphengroup/cms-components/cms-blocks/CardListingPosition/CardListingPosition';
import { CentralModalCI } from '@hyphengroup/cms-components/components/CarInsurance/CentralModalCI/CentralModalCI';
import { useSafeCarInsuranceContext } from '@hyphengroup/cms-components/components/CarInsurance/context/useSafeCarInsurance';
import { ExclusiveRewardsModal } from '@hyphengroup/cms-components/components/CarInsurance/ExclusiveRewards';
import { VEHICLE_TRANSLATIONS } from '@hyphengroup/cms-components/components/CarInsurance/i18n/vehicle';
import { CarInsuranceQuote } from '@hyphengroup/cms-components/components/ProductCard/SectionPremiumCI';
import useExclusiveRewardsModal from '@hyphengroup/cms-components/hooks/useExclusiveRewardsModal';
import {
  PremiumProduct,
  PremiumStatus,
} from '@hyphengroup/cms-components/types/car-insurance';
import { getClientTranslations } from '@hyphengroup/cms-components/utils/translations/getClientTranslations';
import { isVehicleSelectionComplete } from 'car-insurance/utils/isVehicleSelectionComplete';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { CreateLeadSingsaver } from '../../../../../car-insurance/actions/createLead';
import { useClientContext } from '../../../../modules/context/client';
import { useCarInsuranceCentralModal } from '../../../WhatsappStickyModule';
import { pushDataLayer } from '../../pushDataLayer';
import { getMoneymaxQuotes } from './CarInsuranceProviders/MoneymaxProvider';
import { getSingsaverPremiumPrices } from './CarInsuranceProviders/SingsaverProvider';
import { CardListingWrapperProps } from './types';
import { createLead } from '$/(application)/apply/[productId]/server-actions';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';

export const CardListingWrapperCar: React.FC<CardListingWrapperProps> = (
  props,
) => {
  const { metadata, vertical } = props;
  const { pageData } = usePageDataContext();
  const contactMe = pageData.contactMe;

  const { brand, lang } = useClientContext();
  const staticText = getClientTranslations(VEHICLE_TRANSLATIONS, {
    brand,
    lang,
  });
  const isMoneymax = brand === 'moneymax';
  const carInsuranceContext = useSafeCarInsuranceContext();
  const formChanged = carInsuranceContext?.form;

  const { isOpen, toggleModal, openModal } = useCarInsuranceCentralModal(
    vertical ?? 'CAR_INSURANCE',
  );

  const {
    isOpen: isExclusiveRewardsOpen,
    closeModal: closeExclusiveRewardsModal,
    productUid: exclusiveRewardsProductUid,
  } = useExclusiveRewardsModal();

  const [statusRequest, setStatusRequest] = useState<{ status: PremiumStatus }>(
    { status: 'Incomplete' },
  );

  const [carInsurancePremiumProduct, setCarInsurancePremiumProduct] = useState<{
    premiums: PremiumProduct[];
  }>({
    premiums: [],
  });
  const [carInsuranceQuotesMM, setCarInsuranceQuotesMM] = useState<
    CarInsuranceQuote[]
  >([]);

  // An older search's poll chain is never cancelled — its late result must
  // not overwrite a newer search's premiums (mirrors fetchControl in
  // SingSaverProductCardList)
  const fetchControl = useRef<{ id: number }>({ id: 0 });

  // Only the latest fetch settles `isChanged` back to false (CMS-2047).
  const startMoneymaxFetch = () => {
    if (!carInsuranceContext) return;

    const fetchId = ++fetchControl.current.id;

    setStatusRequest({ status: 'Incomplete' });
    getMoneymaxQuotes(carInsuranceContext).then((res) => {
      if (fetchId !== fetchControl.current.id) return;
      const { quotes, status } = res;
      setStatusRequest({ status });
      setCarInsuranceQuotesMM(quotes);
      formChanged?.setIsChanged(false);
    });
  };

  useEffect(() => {
    if (!carInsuranceContext) return;

    // Return before bumping fetchControl: the settle (isChanged -> false) re-runs
    // this effect and must not invalidate the result it just delivered (CMS-2047).
    if (isMoneymax) {
      if (formChanged?.isChanged) startMoneymaxFetch();
      return;
    }

    const fetchId = ++fetchControl.current.id;

    setStatusRequest({ status: 'Incomplete' });
    setCarInsurancePremiumProduct(({ premiums }) => ({
      premiums,
    }));

    getSingsaverPremiumPrices(carInsuranceContext).then((res) => {
      if (fetchId !== fetchControl.current.id) return;
      const { premiums, status } = res;
      setStatusRequest({ status });
      setCarInsurancePremiumProduct({ premiums });
      formChanged?.setIsChanged(false);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formChanged?.isChanged]);

  // Moneymax (CMS-2047): every new complete vehicle requests a refresh.
  const vehicle = carInsuranceContext?.requestParams.vehicle.value;
  const isVehicleComplete = !!vehicle && isVehicleSelectionComplete(vehicle);
  // Keyed on the fields isVehicleSelectionComplete checks.
  const vehicleKey = vehicle
    ? [
        vehicle.makeName,
        vehicle.modelCode,
        vehicle.manufactureYear,
        vehicle.trim,
      ].join('|')
    : '';
  const [requestedVehicleKey, setRequestedVehicleKey] = useState('');
  // Quotes were requested for a vehicle on this visit: the cards stay on screen
  // when a later change empties a field (CMS-2047).
  const hasRequestedQuotes = requestedVehicleKey !== '';
  // The vehicle changed and its refresh is not requested yet: the premiums in
  // state still belong to the previous car.
  const isRefreshPending =
    isMoneymax && isVehicleComplete && vehicleKey !== requestedVehicleKey;

  useEffect(() => {
    if (!isRefreshPending) return;

    setRequestedVehicleKey(vehicleKey);
    setStatusRequest({ status: 'Incomplete' });
    if (formChanged?.isChanged) {
      // A fetch is in flight: start the newer one now, fetchControl drops the
      // older result.
      startMoneymaxFetch();
    } else {
      // The `isChanged` effect above fetches.
      formChanged?.setIsChanged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshPending, vehicleKey]);

  const valueUser = carInsuranceContext?.requestParams.user.value;
  const firstName = valueUser?.firstName;
  const lastName = valueUser?.lastName;
  const email = valueUser?.email;
  const phone = valueUser?.phone;

  useEffect(() => {
    if (brand === 'moneymax' && (!firstName || !lastName || !email || !phone)) {
      openModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const metadataToSend = {
    ...metadata,
    product_card: metadata?.product_card ?? [],
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const serializedMetadataToSend: any = JSON.parse(
    JSON.stringify(metadataToSend),
  );

  // Find the selected product for ExclusiveRewardsModal
  const selectedProduct = exclusiveRewardsProductUid
    ? (metadata?.product_card?.find(
        (p) => p.productBlock?.product?.uid === exclusiveRewardsProductUid,
      )?.productBlock?.product ?? null)
    : null;

  return (
    <>
      {isMoneymax && vehicle && !isVehicleComplete && !hasRequestedQuotes ? (
        <p
          data-component="car-insurance-vehicle-prompt"
          className={classNames('py-8 text-center font-medium text-gray-700', {
            'hidden md:block': !props.cardPosition.visible_mobile,
          })}
        >
          {staticText.enter_vehicle_details_for_premium}
        </p>
      ) : (
        <CardListingPosition
          {...props}
          metadata={serializedMetadataToSend}
          productWithLivePrice={carInsurancePremiumProduct.premiums}
          statusCarInsuranceRequest={
            isRefreshPending ? 'Incomplete' : statusRequest.status
          }
          calculateError={false}
          carInsuranceQuotes={carInsuranceQuotesMM}
          skeletonThreshold={0}
          travelQuotes={undefined}
          travelPremiumsData={undefined}
        />
      )}
      <CentralModalCI
        isOpen={isOpen}
        onClose={toggleModal}
        onOpen={openModal}
        onContactMe={CreateLeadSingsaver}
        pushDataLayer={pushDataLayer}
        contactMe={contactMe}
      />
      <ExclusiveRewardsModal
        isOpen={isExclusiveRewardsOpen}
        onClose={closeExclusiveRewardsModal}
        product={selectedProduct}
        onCreateLead={createLead}
        pushDataLayer={pushDataLayer}
        carInsuranceQuotes={carInsuranceQuotesMM}
        productWithLivePrice={carInsurancePremiumProduct.premiums}
        metadata={serializedMetadataToSend}
      />
    </>
  );
};
